/**
 * EvidenceExportPackDialog — generate a framework-scoped evidence bundle.
 *
 * This is the core workflow for the on-premise Compliance Companion experience:
 * a compliance officer (or their IT admin) selects one or more frameworks, a
 * reporting period, and the included sections, then downloads a bundle they can
 * feed into their existing GRC platform.
 *
 * Outputs (mock):
 *   - Summary text file listing all controls, evidence, and mappings
 *   - Per-evidence listing as CSV
 *   - Optional OSCAL-flavored JSON for federal customers
 *
 * In production this calls /api/trust/evidence-pack with the selected scope
 * and returns a real ZIP including rendered PDFs.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Package, ArrowLeft, ArrowRight, Check, X, Download, Calendar,
  FileCheck, Layers, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  FRAMEWORK_CATEGORY_STYLES,
  type FrameworkId,
} from '@/lib/mock-trust-data';
import { triggerDownload, generateCsv, generateMockPdfContent } from '@/lib/trust-downloads';
import { useTrustFrameworks } from '@/lib/use-trust-frameworks';
import { useTrustEvidence } from '@/lib/use-trust-evidence';

type Period = 'last_30d' | 'last_90d' | 'ytd' | 'last_12m' | 'all_time';

const PERIOD_LABELS: Record<Period, string> = {
  last_30d: 'Last 30 days',
  last_90d: 'Last 90 days',
  ytd: 'Year-to-date',
  last_12m: 'Last 12 months',
  all_time: 'All time',
};

export interface EvidenceExportPackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, pre-select this framework. */
  initialFrameworkId?: FrameworkId | null;
}

export function EvidenceExportPackDialog({
  open, onOpenChange, initialFrameworkId,
}: EvidenceExportPackDialogProps) {
  const { data: FRAMEWORKS = [] } = useTrustFrameworks();
  const { data: EVIDENCE = [] } = useTrustEvidence();
  // Controls catalog used for the per-framework breakdown is currently
  // not exposed across all frameworks via a single endpoint; we render
  // an empty list when the legacy mock array is removed. The UI tolerates
  // an empty controls list. (Per-framework controls available via
  // useFrameworkControls when needed.)
  const CONTROLS: Array<import('@/lib/mock-trust-data').Control> = [];
  const enabledFrameworks = useMemo(() => FRAMEWORKS.filter(f => f.enabled), [FRAMEWORKS]);

  const [selectedFrameworks, setSelectedFrameworks] = useState<Set<FrameworkId>>(() => {
    if (initialFrameworkId) return new Set([initialFrameworkId]);
    return new Set(enabledFrameworks.map(f => f.id));
  });
  const [period, setPeriod] = useState<Period>('last_90d');
  const [includeControls, setIncludeControls] = useState(true);
  const [includeEvidence, setIncludeEvidence] = useState(true);
  const [includeMappings, setIncludeMappings] = useState(true);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [oscal, setOscal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setSelectedFrameworks(
        initialFrameworkId ? new Set([initialFrameworkId]) : new Set(enabledFrameworks.map(f => f.id)),
      );
      setPeriod('last_90d');
      setIncludeControls(true);
      setIncludeEvidence(true);
      setIncludeMappings(true);
      setIncludeSummary(true);
      setOscal(false);
      setGenerating(false);
      setProgress(0);
    }
  }, [open, initialFrameworkId, enabledFrameworks]);

  // Scope preview
  const scopedControls = useMemo(() => {
    return CONTROLS.filter(c => c.mappings.some(m => selectedFrameworks.has(m.framework)));
  }, [selectedFrameworks, CONTROLS]);

  const scopedEvidence = useMemo(() => {
    return EVIDENCE.filter(e => e.controlMappings.some(m => selectedFrameworks.has(m.framework)));
  }, [selectedFrameworks, EVIDENCE]);

  const argusPrimitiveEvidence = useMemo(() => {
    return scopedEvidence.filter(e => !!e.argusAiPrimitive);
  }, [scopedEvidence]);

  const toggleFramework = (id: FrameworkId) => {
    setSelectedFrameworks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedFrameworks.size === enabledFrameworks.length) {
      setSelectedFrameworks(new Set());
    } else {
      setSelectedFrameworks(new Set(enabledFrameworks.map(f => f.id)));
    }
  };

  const canGenerate = selectedFrameworks.size > 0 && (includeControls || includeEvidence || includeMappings || includeSummary);

  const handleGenerate = async () => {
    setGenerating(true);

    // Step 1: compile summary
    setProgress(15);
    await new Promise(r => setTimeout(r, 300));
    const summaryLines: string[] = [];
    summaryLines.push('# ArgusAI Evidence Export Pack');
    summaryLines.push('');
    summaryLines.push(`Generated: ${new Date().toISOString()}`);
    summaryLines.push(`Period: ${PERIOD_LABELS[period]}`);
    summaryLines.push(`Frameworks: ${Array.from(selectedFrameworks).map(id => FRAMEWORKS.find(f => f.id === id)?.shortName).join(', ')}`);
    summaryLines.push('');
    summaryLines.push(`Controls in scope: ${scopedControls.length}`);
    summaryLines.push(`Evidence in scope: ${scopedEvidence.length}`);
    summaryLines.push(`Auto-collected (Argus primitives): ${argusPrimitiveEvidence.length}`);
    summaryLines.push('');
    summaryLines.push('## Framework Health');
    summaryLines.push('');
    Array.from(selectedFrameworks).forEach(id => {
      const fw = FRAMEWORKS.find(f => f.id === id);
      if (!fw) return;
      summaryLines.push(`### ${fw.shortName} — ${fw.fullName}`);
      summaryLines.push(`  Controls passing: ${fw.controlsPassing}/${fw.controlCount}`);
      summaryLines.push(`  Evidence fresh: ${fw.evidenceFresh}/${fw.evidenceCount}`);
      summaryLines.push(`  Status: ${fw.status}`);
      summaryLines.push(`  Last assessment: ${fw.lastAssessmentDate ? new Date(fw.lastAssessmentDate).toLocaleDateString() : '—'}`);
      summaryLines.push('');
    });

    if (includeControls) {
      summaryLines.push('## Controls');
      summaryLines.push('');
      scopedControls.forEach(c => {
        summaryLines.push(`- ${c.name} [${c.status}]`);
        c.mappings
          .filter(m => selectedFrameworks.has(m.framework))
          .forEach(m => {
            summaryLines.push(`    ${m.framework.toUpperCase()}: ${m.controlId} — ${m.controlName}`);
          });
      });
      summaryLines.push('');
    }

    if (includeEvidence) {
      summaryLines.push('## Evidence');
      summaryLines.push('');
      scopedEvidence.forEach(e => {
        summaryLines.push(`- ${e.title} [${e.type}${e.argusAiPrimitive ? ', auto-collected' : ''}]`);
        summaryLines.push(`    Owner: ${e.ownerName} · Uploaded: ${new Date(e.uploadedAt).toLocaleDateString()}`);
        summaryLines.push(`    Version: v${e.versionNumber} · Status: ${e.status}`);
      });
      summaryLines.push('');
    }

    if (includeMappings) {
      summaryLines.push('## Cross-Framework Mappings');
      summaryLines.push('');
      summaryLines.push('One piece of evidence satisfies multiple framework controls simultaneously. This matrix shows how ArgusAI\'s internal controls map across the scoped frameworks.');
      summaryLines.push('');
    }

    setProgress(45);
    await new Promise(r => setTimeout(r, 250));

    // Step 2: summary file
    if (includeSummary) {
      triggerDownload({
        filename: `argus-evidence-pack-summary-${new Date().toISOString().split('T')[0]}.txt`,
        content: generateMockPdfContent('ArgusAI Evidence Export Pack', summaryLines.join('\n')),
        mimeType: 'text/plain',
      });
    }

    setProgress(65);
    await new Promise(r => setTimeout(r, 200));

    // Step 3: controls CSV
    if (includeControls) {
      triggerDownload({
        filename: `argus-controls-${new Date().toISOString().split('T')[0]}.csv`,
        content: generateCsv(
          ['Control', 'Family', 'Status', 'Priority', 'Owner', 'Last Tested', 'Last Result', 'Argus Primitive'],
          scopedControls.map(c => [
            c.name, c.family, c.status, c.priority, c.ownerName,
            c.lastTestedAt ?? '',
            c.lastResult ?? '',
            c.argusAiPrimitive ?? '',
          ]),
        ),
        mimeType: 'text/csv',
      });
    }

    setProgress(80);
    await new Promise(r => setTimeout(r, 200));

    // Step 4: evidence CSV
    if (includeEvidence) {
      triggerDownload({
        filename: `argus-evidence-${new Date().toISOString().split('T')[0]}.csv`,
        content: generateCsv(
          ['Title', 'Type', 'Source', 'Owner', 'Uploaded', 'Version', 'Status', 'Mapped Frameworks', 'Argus Primitive'],
          scopedEvidence.map(e => [
            e.title, e.type, e.source, e.ownerName, e.uploadedAt, e.versionNumber, e.status,
            Array.from(new Set(e.controlMappings.filter(m => selectedFrameworks.has(m.framework)).map(m => m.framework))).join(' | '),
            e.argusAiPrimitive ?? '',
          ]),
        ),
        mimeType: 'text/csv',
      });
    }

    setProgress(90);
    await new Promise(r => setTimeout(r, 150));

    // Step 5: mappings CSV
    if (includeMappings) {
      const rows: Array<Array<string | number | null>> = [];
      scopedControls.forEach(c => {
        c.mappings
          .filter(m => selectedFrameworks.has(m.framework))
          .forEach(m => {
            rows.push([c.name, c.status, m.framework, m.controlId, m.controlName, m.relevance]);
          });
      });
      triggerDownload({
        filename: `argus-control-mappings-${new Date().toISOString().split('T')[0]}.csv`,
        content: generateCsv(['Control', 'Control Status', 'Framework', 'Framework Control ID', 'Framework Control Name', 'Relevance'], rows),
        mimeType: 'text/csv',
      });
    }

    // Step 6: OSCAL JSON (optional)
    if (oscal) {
      const oscalDoc = {
        oscal_version: '1.1.2',
        system_security_plan: {
          uuid: `argusai-ssp-${Date.now()}`,
          metadata: {
            title: 'ArgusAI System Security Plan (machine-readable)',
            last_modified: new Date().toISOString(),
            version: '1.0',
          },
          import_profile: Array.from(selectedFrameworks),
          system_characteristics: {
            system_name: 'ArgusAI Enterprise AI Platform',
            description: 'On-premise single-tenant AI platform with built-in compliance primitives.',
          },
          control_implementation: scopedControls.map(c => ({
            uuid: c.id,
            control_id: c.id,
            description: c.description,
            implementation_status: c.status,
            argus_primitive: c.argusAiPrimitive,
            framework_mappings: c.mappings.filter(m => selectedFrameworks.has(m.framework)),
          })),
        },
      };
      triggerDownload({
        filename: `argus-oscal-ssp-${new Date().toISOString().split('T')[0]}.json`,
        content: JSON.stringify(oscalDoc, null, 2),
        mimeType: 'application/json',
      });
    }

    setProgress(100);
    await new Promise(r => setTimeout(r, 300));

    toast.success('Evidence pack generated', {
      description: `${scopedControls.length} controls · ${scopedEvidence.length} evidence items · ${selectedFrameworks.size} framework${selectedFrameworks.size === 1 ? '' : 's'}`,
    });
    setGenerating(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Generate Evidence Export Pack
          </DialogTitle>
          <DialogDescription>
            Bundle evidence from ArgusAI into a framework-scoped package for your GRC platform or external auditor.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 py-2">
          {/* ── Framework Selection ──────────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Frameworks to Include</Label>
              <Button variant="ghost" size="sm" className="text-[10px] h-6" onClick={toggleAll}>
                {selectedFrameworks.size === enabledFrameworks.length ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {enabledFrameworks.map(fw => {
                const selected = selectedFrameworks.has(fw.id);
                const cs = FRAMEWORK_CATEGORY_STYLES[fw.category];
                return (
                  <label
                    key={fw.id}
                    className={cn(
                      'flex items-start gap-2 p-2 rounded-md border cursor-pointer text-xs',
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
                    )}
                  >
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => toggleFramework(fw.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{fw.shortName}</span>
                        <Badge variant="outline" className={cn('text-[9px] px-1 py-0', cs.color)}>{cs.label}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate">{fw.controlCount} controls</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>

          {/* ── Reporting Period ─────────────────────────────────────────── */}
          <section>
            <Label className="text-xs">Reporting Period</Label>
            <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <SelectTrigger className="mt-1.5 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
                  <SelectItem key={p} value={p} className="text-xs">{PERIOD_LABELS[p]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Evidence with an effective date inside this window will be included. Controls are always current state.
            </p>
          </section>

          {/* ── Sections ─────────────────────────────────────────────────── */}
          <section>
            <Label className="text-xs mb-2 block">Sections</Label>
            <div className="space-y-1.5">
              <SectionRow
                icon={FileText}
                label="Summary report"
                description="Human-readable markdown summarizing frameworks, controls, and evidence"
                checked={includeSummary}
                onToggle={() => setIncludeSummary(v => !v)}
              />
              <SectionRow
                icon={FileCheck}
                label="Controls ({count})"
                description="Every in-scope control with status, owner, test results"
                count={scopedControls.length}
                checked={includeControls}
                onToggle={() => setIncludeControls(v => !v)}
              />
              <SectionRow
                icon={Package}
                label="Evidence ({count})"
                description="Evidence records with provenance and framework tags"
                count={scopedEvidence.length}
                checked={includeEvidence}
                onToggle={() => setIncludeEvidence(v => !v)}
              />
              <SectionRow
                icon={Layers}
                label="Cross-framework mappings"
                description="How one control satisfies multiple frameworks"
                checked={includeMappings}
                onToggle={() => setIncludeMappings(v => !v)}
              />
            </div>

            <label className="flex items-start gap-2 mt-3 p-2.5 rounded-md border border-dashed cursor-pointer">
              <Checkbox checked={oscal} onCheckedChange={(c) => setOscal(c === true)} className="mt-0.5" />
              <div>
                <p className="text-xs font-medium">Include OSCAL JSON (for federal customers)</p>
                <p className="text-[10px] text-muted-foreground">
                  Machine-readable System Security Plan per NIST OSCAL 1.1.2 — required for FedRAMP / StateRAMP submissions.
                </p>
              </div>
            </label>
          </section>

          {/* ── Summary card ─────────────────────────────────────────────── */}
          <section className="rounded-lg border bg-gradient-to-br from-primary/5 to-primary/0 p-3">
            <p className="text-xs font-semibold mb-1.5">Package contents</p>
            <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground">
              <div>Frameworks: <strong className="text-foreground">{selectedFrameworks.size}</strong></div>
              <div>Period: <strong className="text-foreground">{PERIOD_LABELS[period]}</strong></div>
              <div>Controls: <strong className="text-foreground">{includeControls ? scopedControls.length : 0}</strong></div>
              <div>Evidence: <strong className="text-foreground">{includeEvidence ? scopedEvidence.length : 0}</strong></div>
              <div>Auto-collected: <strong className="text-foreground">{includeEvidence ? argusPrimitiveEvidence.length : 0}</strong></div>
              <div>OSCAL: <strong className="text-foreground">{oscal ? 'Yes' : 'No'}</strong></div>
            </div>
          </section>

          {/* ── Progress bar while generating ────────────────────────────── */}
          {generating && (
            <div className="rounded-md border p-3 bg-muted/20">
              <div className="flex items-center gap-2 mb-1.5">
                <Download className="h-3.5 w-3.5 animate-pulse text-primary" />
                <p className="text-xs font-medium">Assembling pack…</p>
              </div>
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{progress}%</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={generating}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canGenerate || generating}
            onClick={handleGenerate}
            className="gap-1.5"
          >
            {generating ? 'Generating…' : (<><Download className="h-3.5 w-3.5" /> Generate & Download</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionRow({
  icon: Icon, label, description, count, checked, onToggle,
}: {
  icon: typeof Package;
  label: string;
  description: string;
  count?: number;
  checked: boolean;
  onToggle: () => void;
}) {
  const resolvedLabel = count !== undefined ? label.replace('{count}', String(count)) : label.replace(' ({count})', '');
  return (
    <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/20">
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" />
      <Icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium">{resolvedLabel}</p>
        <p className="text-[10px] text-muted-foreground">{description}</p>
      </div>
    </label>
  );
}
